import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import { useCurrencies } from '../hooks/useCurrencies';
import { setFormatSettings } from '../utils/numberFormat';
import { Button, FormField } from '../components/ui';
import LangSwitcher from '../components/LangSwitcher';
import EDocumentSettingsPage from './EDocumentSettingsPage';
import WarehouseEDocSettings from '../components/WarehouseEDocSettings';

const EDocumentSettingsEmbedded = () => <EDocumentSettingsPage embedded />;

function WarehouseEDocSection({ warehouses }) {
  const [whId, setWhId] = React.useState(warehouses[0]?.id || '');
  React.useEffect(() => {
    if (!whId && warehouses.length) setWhId(warehouses[0].id);
  }, [warehouses, whId]);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, flex: 1 }}>📦 Depo Bazlı e-Belge Tanımları</h2>
        <FormField label="" hint="" >
          <select value={whId} onChange={e => setWhId(e.target.value)} style={{ minWidth: 240 }}>
            <option value="">— Depo Seçin —</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} - {w.name}</option>)}
          </select>
        </FormField>
      </div>
      <div style={{ marginBottom: 12, padding: 10, background: 'rgba(59,130,246,0.08)', borderLeft: '3px solid var(--info)', borderRadius: 4, fontSize: 13 }}>
        💡 Her depo × her e-belge tipi için ayrı: <strong>aktif mi</strong>, <strong>belge no öneki</strong> ve <strong>sıra numarası</strong>. Fatura kesilirken depodan otomatik tespit edilir.
      </div>
      {whId && <WarehouseEDocSettings warehouseId={whId} canEdit={true} />}
    </div>
  );
}

function SettingsPage() {
  const { t } = useTranslation();
  const { currencies } = useCurrencies();
  const [settings, setSettings] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchSettings = async () => {
    try {
      const [sRes, wRes, cRes, aRes, pRes, bRes] = await Promise.all([
        api.get('/settings'),
        api.get('/warehouses'),
        api.get('/cash-registers'),
        api.get('/accounts?limit=500'),
        api.get('/personnel?limit=500').catch(() => ({ data: { success: false } })),
        api.get('/branches?is_active=yes').catch(() => ({ data: { success: false } })),
      ]);
      if (sRes.data.success) {
        setSettings(sRes.data.data);
        setFormatSettings(sRes.data.data);
      }
      if (wRes.data.success) setWarehouses(wRes.data.data.items || []);
      if (cRes.data.success) setCashRegisters(cRes.data.data.items || []);
      if (aRes.data?.success) setAccounts(aRes.data.data?.items || aRes.data.data || []);
      if (pRes.data?.success) setPersonnel(pRes.data.data?.items || pRes.data.data || []);
      if (bRes.data?.success) setBranches(bRes.data.data?.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const saveSetting = async (key, value) => {
    setSaving(true); setMsg(null);
    try {
      await api.put('/settings/' + key, { value });
      setSettings(prev => {
        const next = { ...prev, [key]: value };
        if (key === 'quantity_decimals' || key === 'amount_decimals') setFormatSettings(next);
        return next;
      });
      setMsg({ type: 'success', text: 'Ayar kaydedildi' });
      setTimeout(() => setMsg(null), 2000);
    } catch (e) { setMsg({ type: 'error', text: 'Hata: ' + (e.response?.data?.message || e.message) }); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{padding:40,textAlign:'center',color:'var(--text-tertiary)'}}>{t('common.loading')}</div>;

  return (
    <div className="settings-page" style={{padding:24,maxWidth:800,margin:'0 auto'}}>
      <h1 style={{fontSize:'1.5rem',fontWeight:700,marginBottom:8}}>{t('settings.title')}</h1>
      <p style={{color:'var(--text-secondary)',marginBottom:24,fontSize:'0.9rem'}}>Firma genel ayarlari. Buradaki secimler fatura ve belgelerde varsayilan olarak kullanilir.</p>

      {msg && <div className={'detail-msg ' + msg.type} style={{marginBottom:16}}>{msg.text}</div>}

      {/* DİL AYARI — En üstte, herkes kolay bulsun */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
        <div style={{flex:1,minWidth:200}}>
          <h2 style={{fontSize:'1.1rem',fontWeight:600,margin:0}}>🌐 {t('settings.language')}</h2>
          <p style={{color:'var(--text-secondary)',marginTop:4,marginBottom:0,fontSize:'0.85rem'}}>
            {t('lang.switchTo')}
          </p>
        </div>
        <LangSwitcher variant="compact" />
      </div>

      {/* GENEL AYARLAR */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:600,marginBottom:16}}>{t('settings.general')}</h2>
        
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <FormField label="Yerel Para Birimi" hint="Faturalarda ve raporlarda varsayilan para birimi">
            <select
              value={settings.local_currency || 'TRY'}
              onChange={e => saveSetting('local_currency', e.target.value)}
            >
              {currencies.map(c => <option key={c.id||c.code} value={c.code}>{c.code} - {c.name}</option>)}
            </select>
          </FormField>

          <FormField label="Varsayilan Depo" hint="Fatura ve islemlerde otomatik gelen depo">
            <select
              value={settings.default_warehouse_id || ''}
              onChange={e => saveSetting('default_warehouse_id', e.target.value)}
            >
              <option value="">Seciniz (her seferinde sec)</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} - {w.name}</option>)}
            </select>
          </FormField>

          <FormField label="Varsayılan Kasa" hint="Kasa hareketleri/açılış/virman işlemlerinde otomatik gelen kasa. Boşsa ilk kasa kullanılır.">
            <select
              value={settings.default_cash_register_id || ''}
              onChange={e => saveSetting('default_cash_register_id', e.target.value)}
            >
              <option value="">Seciniz (ilk kasa kullanılır)</option>
              {cashRegisters.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
            </select>
          </FormField>

          <FormField label="Terazi Kullan" hint="Barkod okutmada tartili urun otomatik taninir">
            <select
              value={settings.use_scale || 'false'}
              onChange={e => saveSetting('use_scale', e.target.value)}
            >
              <option value="false">Kapali</option>
              <option value="true">Acik</option>
            </select>
          </FormField>

          <FormField label="Miktar Ondalık Sayısı" hint="Stok miktarı, fatura kalem miktarı vb.">
            <select
              value={settings.quantity_decimals != null ? String(settings.quantity_decimals) : '2'}
              onChange={e => saveSetting('quantity_decimals', e.target.value)}
            >
              {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} basamak</option>)}
            </select>
          </FormField>

          <FormField label="Tutar Ondalık Sayısı" hint="Fiyat, tutar, bakiye, toplamlar (döviz kuru hariç — sabit 5)">
            <select
              value={settings.amount_decimals != null ? String(settings.amount_decimals) : '2'}
              onChange={e => saveSetting('amount_decimals', e.target.value)}
            >
              {[0,1,2,3,4].map(n => <option key={n} value={n}>{n} basamak</option>)}
            </select>
          </FormField>
        </div>
      </div>

      {/* STOK KARTI AYARLARI */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:600,marginBottom:16}}>Stok Karti Ayarları</h2>
        
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <FormField label="Renk / Beden Takibi">
            <select
              value={settings.color_size_tracking || 'false'}
              onChange={e => saveSetting('color_size_tracking', e.target.value)}
            >
              <option value="false">Kapali</option>
              <option value="true">Acik - Faturada Renk/Beden kolonu gorunur</option>
            </select>
          </FormField>

          <FormField label="Negatif Stok Denetimi (Ana Anahtar)" hint="Açık: stok kartındaki 'negatif stoğa izin' ayarı geçerli olur — kartı açık ürün negatife düşer, kartı kapalı üründe yetersiz stokta DURDURUR. Kapalı: denetim yok, yetersiz stokta bile satış DEVAM EDER. Tüm satışlarda geçerlidir.">
            <select
              value={settings.negative_stock_control != null ? settings.negative_stock_control : 'true'}
              onChange={e => saveSetting('negative_stock_control', e.target.value)}
            >
              <option value="true">Acik - Stok kartı iznine uy (kart kapalıysa yetersizde durdur)</option>
              <option value="false">Kapali - Denetim yok, yetersiz stokta da satışa devam et</option>
            </select>
          </FormField>

          <FormField label="Varsayilan KDV Orani (%)" hint="Yeni stok karti olusturulurken varsayilan KDV orani">
            <select
              value={settings.default_vat_rate || '20'}
              onChange={e => saveSetting('default_vat_rate', e.target.value)}
            >
              <option value="0">%0</option>
              <option value="1">%1</option>
              <option value="10">%10</option>
              <option value="20">%20</option>
            </select>
          </FormField>

          <FormField label="Tartili Ürün Başlangıç Kodu" hint="Tartili urun barkodlarinin basladigi rakam/kod (orn: 2)">
            <input
              type="text"
              value={settings.weighted_product_start_code || ''}
              onChange={e => saveSetting('weighted_product_start_code', e.target.value)}
              placeholder="Orn: 2"
            />
          </FormField>
        </div>
      </div>

      {/* BARKOD AYARLARI */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:600,marginBottom:16}}>Barkod Ayarları</h2>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <FormField label="Barkod Tipi">
            <select
              value={settings.barcode_type || 'ean13'}
              onChange={e => saveSetting('barcode_type', e.target.value)}
            >
              <option value="ean13">EAN-13 (3 ülke + 9 sıra + 1 check)</option>
              <option value="ean8">EAN-8 (2 ülke + 5 sıra + 1 check)</option>
              <option value="code39">Code 39</option>
              <option value="code128">Code 128</option>
            </select>
          </FormField>

          <FormField label="Ülke / Önek Kodu" hint="Türkiye için 869 (varsayılan). EAN-13'te ilk 3 hane.">
            <input
              type="text"
              value={settings.barcode_country_code || '869'}
              onChange={e => saveSetting('barcode_country_code', e.target.value.replace(/\D/g,''))}
              placeholder="869"
              maxLength={3}
              style={{fontFamily:'monospace'}}
            />
          </FormField>

          <FormField label="Son Verilen Barkod Numarası" hint="Sıradaki barkod bunun +1'i ile üretilir. Manuel düzenlenebilir.">
            <input
              type="number"
              min={0}
              value={settings.barcode_last_number || '0'}
              onChange={e => saveSetting('barcode_last_number', String(parseInt(e.target.value)||0))}
              style={{fontFamily:'monospace'}}
            />
          </FormField>

          <FormField label="Otomatik Barkod (Stok için)" hint="Renk/Beden Sihirbazı'ndaki yeni varyantlar için de geçerli.">
            <select
              value={settings.barcode_auto || 'false'}
              onChange={e => saveSetting('barcode_auto', e.target.value)}
            >
              <option value="false">Kapali</option>
              <option value="true">Acik - Stok kaydında barkod boşsa otomatik üret</option>
            </select>
          </FormField>
        </div>
      </div>

      {/* FATURA AYARLARI */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem',fontWeight:600,marginBottom:16}}>Fatura Ayarları</h2>
        
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          <FormField label="Varsayilan Vade (gun)">
            <input
              type="number"
              value={settings.default_payment_days || '0'}
              onChange={e => saveSetting('default_payment_days', e.target.value)}
            />
          </FormField>

          <FormField label="e-Fatura Varsayilan Senaryo">
            <select
              value={settings.default_einvoice_scenario || ''}
              onChange={e => saveSetting('default_einvoice_scenario', e.target.value)}
            >
              <option value="">Seciniz</option>
              <option value="TEMEL">TEMEL</option>
              <option value="TICARI">TICARI</option>
            </select>
          </FormField>
        </div>
      </div>

      {/* PERAKENDE SATIŞ AYARLARI */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 8 }}>🛒 {t('settings.retailSettings')}</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: '0.85rem' }}>
          Hızlı perakende satış (POS) ekranında varsayılan değerler. Buradaki cari her satışın muhasebe kaydı için kullanılır.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Varsayılan Cari (Perakende Müşteri) *"
            hint="Misafir satışları için cari kart. Bu seçilmeden perakende satış kaydedilemez.">
            <select
              value={settings.retail_default_account_id || ''}
              onChange={e => saveSetting('retail_default_account_id', e.target.value)}
              style={{ borderColor: !settings.retail_default_account_id ? 'var(--warning)' : undefined }}
            >
              <option value="">— Cari seçin —</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.code} - {a.title || a.name}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Varsayılan Depo (Perakende)"
            hint="Boş bırakılırsa genel 'Varsayılan Depo' ayarı kullanılır.">
            <select
              value={settings.retail_default_warehouse_id || ''}
              onChange={e => saveSetting('retail_default_warehouse_id', e.target.value)}
            >
              <option value="">— Genel ayarı kullan —</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.code} - {w.name}</option>)}
            </select>
          </FormField>

          <FormField label="Varsayılan Şube (Perakende)"
            hint="Bu cihazdan yapılan perakende satışlar bu şubeye atanır. Raporlamada şube bazlı ayırmak için kullanılır.">
            <select
              value={settings.retail_default_branch_id || ''}
              onChange={e => saveSetting('retail_default_branch_id', e.target.value)}
            >
              <option value="">— Şube yok —</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.code ? b.code + ' - ' : ''}{b.name}</option>)}
            </select>
          </FormField>

          <FormField label="Varsayılan Kasa (Perakende)"
            hint="Nakit tahsilatların düşeceği kasa. Boş ise genel 'Varsayılan Kasa' kullanılır.">
            <select
              value={settings.retail_default_cash_register_id || ''}
              onChange={e => saveSetting('retail_default_cash_register_id', e.target.value)}
            >
              <option value="">— Genel ayarı kullan —</option>
              {cashRegisters.map(c => <option key={c.id} value={c.id}>{c.register_name}</option>)}
            </select>
          </FormField>

          <FormField label="Varsayılan Satış Personeli"
            hint="POS'ta satıra otomatik atanacak personel. Boş bırakılabilir.">
            <select
              value={settings.retail_default_personnel_id || ''}
              onChange={e => saveSetting('retail_default_personnel_id', e.target.value)}
            >
              <option value="">— Yok —</option>
              {personnel.map(p => <option key={p.id} value={p.id}>{p.code} - {p.full_name}</option>)}
            </select>
          </FormField>

          <FormField label="Çekmece Otomatik Açılsın"
            hint="Nakit ödemede yazıcıya çekmece komutu gönderilsin mi.">
            <select
              value={settings.retail_open_drawer_on_cash || 'false'}
              onChange={e => saveSetting('retail_open_drawer_on_cash', e.target.value)}
            >
              <option value="false">Hayır</option>
              <option value="true">Evet</option>
            </select>
          </FormField>

          <FormField label="Fişi Otomatik Yazdır"
            hint="Satış tamamlanınca fişi otomatik yazıcıya gönder.">
            <select
              value={settings.retail_auto_print_receipt || 'false'}
              onChange={e => saveSetting('retail_auto_print_receipt', e.target.value)}
            >
              <option value="false">Hayır</option>
              <option value="true">Evet</option>
            </select>
          </FormField>

          <FormField label="İskonto Seviye Sayısı"
            hint="POS'ta kaç farklı iskonto alanı açılsın (her biri % veya Tutar).">
            <select
              value={settings.retail_discount_levels || '1'}
              onChange={e => saveSetting('retail_discount_levels', e.target.value)}
            >
              <option value="0">Kapalı</option>
              <option value="1">1 (Tek iskonto)</option>
              <option value="2">2 İskonto</option>
              <option value="3">3 İskonto</option>
            </select>
          </FormField>

          <FormField label="Yuvarlama Aktif"
            hint="POS'ta yuvarlama butonu. Kayıtta line discount_rate5 olarak yansır.">
            <select
              value={settings.retail_rounding_enabled || 'true'}
              onChange={e => saveSetting('retail_rounding_enabled', e.target.value)}
            >
              <option value="true">Evet — açık</option>
              <option value="false">Hayır</option>
            </select>
          </FormField>

          <FormField label="Alış Veriş Çeki Kullanılsın"
            hint="Açık → Perakende satış/iade ile cari tahsilat/tediye makbuzlarında 'Alış Veriş Çeki' ödeme tipi görünür. Kapalı → hiçbir yerde görünmez.">
            <select
              value={settings.shopping_voucher_enabled || 'false'}
              onChange={e => saveSetting('shopping_voucher_enabled', e.target.value)}
            >
              <option value="false">Hayır — kapalı</option>
              <option value="true">Evet — açık</option>
            </select>
          </FormField>

          <FormField label="Çek Kullanım Günü"
            hint="Yeni alışveriş çeki üretildiğinde son kullanım tarihi = veriliş tarihi + bu gün sayısı. Boş veya 0 → vade atılmaz (süresiz).">
            <input type="number" min={0}
              value={settings.shopping_voucher_validity_days || ''}
              onChange={e => saveSetting('shopping_voucher_validity_days', e.target.value)}
              placeholder="Örn 180" />
          </FormField>
        </div>
      </div>

      {/* KART + NUMARA SERILERI — Tek kompakt tablo */}
      <SeriesTable settings={settings} saveSetting={saveSetting} />

      {/* SMTP / E-POSTA */}
      <SmtpSettings
        settings={settings}
        saveSetting={saveSetting}
        setSettings={setSettings}
        setMsg={setMsg}
      />

      {/* e-BELGE AYARLARI */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <EDocumentSettingsEmbedded />
      </div>

      {/* DEPO BAZLI e-BELGE TANIMLARI */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <WarehouseEDocSection warehouses={warehouses} />
      </div>

      {/* ONAY HİYERARŞİSİ */}
      <div style={{background:'var(--bg-card)',border:'1px solid var(--border-secondary)',borderRadius:14,padding:20,marginBottom:16}}>
        <ApprovalHierarchySection />
      </div>

      {/* AI ASİSTAN */}
      <AiSettings setMsg={setMsg} />
    </div>
  );
}

// Kart + Numara Serileri — kompakt tablo (kategori başlıklı)
function SeriesTable({ settings, saveSetting }) {
  // Prefix son durumları — son üretilen belge numarası ve sayaç
  const [prefixStatus, setPrefixStatus] = React.useState({});
  const loadStatus = React.useCallback(() => {
    api.get('/settings/prefix-status').then(r => {
      if (r.data?.success) setPrefixStatus(r.data.data || {});
    }).catch(() => {});
  }, []);
  React.useEffect(() => { loadStatus(); }, [loadStatus]);

  const resetSeq = async (key) => {
    const cur = prefixStatus[key]?.last_seq || 0;
    const input = window.prompt(`"${key}" için sonraki belgenin alacağı NUMARAYI girin (mevcut son: ${cur})`, String(cur + 1));
    if (input == null) return;
    const newSeq = parseInt(input);
    if (isNaN(newSeq) || newSeq < 0) { alert('Geçersiz sayı'); return; }
    try {
      // new_seq = next belgenin alacağı no - 1 (backend max(db, min)+1 hesaplıyor)
      await api.post('/settings/prefix-status/reset', { key, new_seq: newSeq - 1 });
      alert(`✓ Sonraki belge: ${newSeq}`);
      loadStatus();
    } catch (e) { alert(e.response?.data?.message || 'Sıfırlama başarısız'); }
  };
  const year = new Date().getFullYear();
  const groups = [
    {
      title: 'Kart Serileri',
      hint: 'Yeni kayıt açarken kod boş bırakılırsa otomatik üretilir.',
      example: (p) => p + '00001',
      rows: [
        { key: 'prefix_stock',     label: 'Stok Kartı',     def: 'STK' },
        { key: 'prefix_account',   label: 'Cari Kartı',     def: 'CRI' },
        { key: 'prefix_personnel', label: 'Personel Kartı', def: 'PRS' },
      ],
    },
    {
      title: 'Fatura Numaraları',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '00001',
      rows: [
        { key: 'prefix_invoice_sales',              label: 'Satış Faturası',                def: 'SF' },
        { key: 'prefix_invoice_retail_sale',        label: 'Perakende Satış Faturası',      def: 'PSF' },
        { key: 'prefix_invoice_sales_return',       label: 'Satış İade Faturası',           def: 'SIF' },
        { key: 'prefix_invoice_retail_sale_return', label: 'Perakende Satış İade Faturası', def: 'PSIF' },
        { key: 'prefix_invoice_purchase',           label: 'Alış Faturası',                  def: 'AF' },
        { key: 'prefix_invoice_purchase_return',    label: 'Alış İade Faturası',             def: 'AIF' },
        // Verilen / Alınan Teklif (_waybillCore üzerinden)
        { key: 'prefix_sales_quotes_forward',       label: 'Verilen Teklif',                  def: 'VT' },
        { key: 'prefix_sales_quotes_return',        label: 'Verilen Teklif İade',             def: 'VTI' },
        { key: 'prefix_purchase_quotes_forward',    label: 'Alınan Teklif',                   def: 'AT' },
        { key: 'prefix_purchase_quotes_return',     label: 'Alınan Teklif İade',              def: 'ATI' },
        // İrsaliyeler (_waybillCore üzerinden)
        { key: 'prefix_sales_waybills_forward',     label: 'Satış İrsaliyesi',                def: 'SI' },
        { key: 'prefix_sales_waybills_return',      label: 'Satış İade İrsaliyesi',           def: 'SII' },
        { key: 'prefix_purchase_waybills_forward',  label: 'Alış İrsaliyesi',                 def: 'AI' },
        { key: 'prefix_purchase_waybills_return',   label: 'Alış İade İrsaliyesi',            def: 'AII' },
        // Siparişler (_waybillCore üzerinden) — varsa
        { key: 'prefix_sales_orders_forward',       label: 'Satış Siparişi',                  def: 'SS' },
        { key: 'prefix_purchase_orders_forward',    label: 'Alış Siparişi',                   def: 'AS' },
      ],
    },
    {
      title: 'Banka Dekontları',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '00001',
      rows: [
        { key: 'prefix_bank_opening', label: 'Banka Açılış Fişi',     def: 'BAF' },
        { key: 'prefix_bank_credit',  label: 'Banka Alacak Dekontu',  def: 'BAD' },
        { key: 'prefix_bank_debit',   label: 'Banka Borç Dekontu',    def: 'BBD' },
        { key: 'prefix_bank_virman',  label: 'Banka Virman Dekontu',  def: 'BVD' },
      ],
    },
    {
      title: 'Stok Hareket Belgeleri',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '0000001',
      rows: [
        { key: 'prefix_warehouse_transfer', label: 'Depo Nakil Fişi',  def: 'DNF' },
        { key: 'prefix_stock_count',        label: 'Stok Sayım Fişi',  def: 'SAY' },
        { key: 'prefix_count_surplus',      label: 'Sayım Fazlası Fişi', def: 'SFZ' },
        { key: 'prefix_count_deficit',      label: 'Sayım Eksiği Fişi',  def: 'SEK' },
        { key: 'prefix_waste',              label: 'Fire Fişi',          def: 'FRE' },
      ],
    },
    {
      title: 'Kasa Dekontları',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '00001',
      rows: [
        { key: 'prefix_cash_opening', label: 'Kasa Açılış Fişi',    def: 'KAF' },
        { key: 'prefix_cash_virman',  label: 'Kasa Virman Dekontu', def: 'KVD' },
      ],
    },
    {
      title: 'Cari Dekontları',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '00001',
      rows: [
        { key: 'prefix_cari_opening',     label: 'Cari Açılış Fişi',       def: 'CAF' },
        { key: 'prefix_cari_debit',       label: 'Cari Borç Dekontu',      def: 'CBD' },
        { key: 'prefix_cari_credit',      label: 'Cari Alacak Dekontu',    def: 'CAD' },
        { key: 'prefix_cari_virman',      label: 'Cari Virman Dekontu',    def: 'CVD' },
        { key: 'prefix_cari_receipt',     label: 'Cari Tahsilat Makbuzu',  def: 'TH' },
        { key: 'prefix_shopping_voucher', label: 'Alış Veriş Çeki',        def: 'AVC' },
      ],
    },
    {
      title: 'Personel Dekontları',
      hint: 'Belge no otomatik üretilir.',
      example: (p) => p + year + '00001',
      rows: [
        { key: 'prefix_personnel_credit', label: 'Personel Alacak Dekontu', def: 'PAD' },
        { key: 'prefix_personnel_debit',  label: 'Personel Borç Dekontu',   def: 'PBD' },
      ],
    },
  ];

  const cellStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', borderBottom: '1px solid var(--border-secondary)' };
  const headStyle = { padding: '10px 12px', fontSize: 'var(--text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-tertiary)', background: 'var(--bg-secondary)', textAlign: 'left', borderBottom: '1px solid var(--border-secondary)', position: 'sticky', top: 0, zIndex: 2 };
  // Grup başlığı zemini — accent-glow ile belirgin renklenmiş bant (scroll ile hareket eder)
  const groupHeadStyle = {
    padding: '8px 12px',
    background: 'var(--accent-glow)',
    color: 'var(--accent-primary)',
    fontSize: 'var(--text-xs)',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderTop: '1px solid var(--border-primary)',
    borderBottom: '1px solid var(--border-primary)',
  };

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Kart & Numara Serileri</h2>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
          Belgelerin önek + sıra numarası kombinasyonu burada yönetilir. Önek değişikliği sadece sonraki belgeleri etkiler.
        </p>
      </div>
      {/* Scroll konteynerinde — sayfayı uzatmak yerine kendi içinde scroll yapar */}
      <div style={{ maxHeight: 480, overflowY: 'auto', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr>
              <th style={headStyle}>Belge / Kart Tipi</th>
              <th style={{ ...headStyle, width: 110 }}>Ön Ek</th>
              <th style={{ ...headStyle, width: 90, textAlign: 'center' }}>Dönem</th>
              <th style={{ ...headStyle, width: 70, textAlign: 'center' }}>Hane</th>
              <th style={{ ...headStyle, width: 200 }}>Örnek</th>
              <th style={{ ...headStyle, width: 180 }}>Son Belge No</th>
              <th style={{ ...headStyle, width: 100, textAlign: 'center' }}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(g => (
              <React.Fragment key={g.title}>
                <tr>
                  <td colSpan={7} style={groupHeadStyle}>
                    {g.title}
                  </td>
                </tr>
                {g.rows.map(r => {
                  const v = settings[r.key] || r.def;
                  const status = prefixStatus[r.key];
                  const hasStatus = !!status;
                  const baseSuffix = r.key.replace(/^prefix_/, '');
                  const yearKey = 'numseries_include_year_' + baseSuffix;
                  const lenKey  = 'numseries_digit_length_' + baseSuffix;
                  const includeYear = status ? !!status.include_year
                    : (settings[yearKey] != null ? String(settings[yearKey]).toLowerCase() === 'true' : true);
                  const digitLen = status?.digit_length || parseInt(settings[lenKey]) || 10;
                  return (
                    <tr key={r.key} style={{ transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={cellStyle}>{r.label}</td>
                      <td style={cellStyle}>
                        <input
                          className="ui-input"
                          value={v}
                          onChange={e => saveSetting(r.key, e.target.value)}
                          maxLength={10}
                          style={{ fontFamily: 'JetBrains Mono, monospace', width: 100, padding: '5px 8px', fontSize: 'var(--text-xs)' }}
                        />
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <input type="checkbox" checked={includeYear}
                          onChange={e => { saveSetting(yearKey, String(e.target.checked)); setTimeout(loadStatus, 200); }}
                          title="Ön ekten sonra yıl eklensin mi?" />
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <input type="number" min="1" max="20" value={digitLen}
                          onChange={e => { saveSetting(lenKey, e.target.value); setTimeout(loadStatus, 200); }}
                          style={{ width: 56, fontFamily: 'JetBrains Mono, monospace', padding: '4px 6px', fontSize: 'var(--text-xs)', textAlign: 'right' }}
                          title="Sıra numarasının hane sayısı" />
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        {v}{includeYear ? new Date().getFullYear() : ''}{'1'.padStart(digitLen, '0')}
                      </td>
                      <td style={{ ...cellStyle, fontFamily: 'JetBrains Mono, monospace', fontSize: 'var(--text-xs)' }}>
                        {hasStatus ? (
                          <>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{status.last || '— henüz yok'}</div>
                            {status.last_seq > 0 && (
                              <div style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>Sayaç: {status.last_seq} · Sonraki: {status.last_seq + 1}</div>
                            )}
                          </>
                        ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        {hasStatus && (
                          <button onClick={() => resetSeq(r.key)}
                            title="Sonraki belge numarasını ayarla"
                            style={{
                              background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)',
                              padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                            }}>↺ Ayarla</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SmtpSettings({ settings, saveSetting, setSettings, setMsg }) {
  const [pwd, setPwd] = React.useState('');
  const [testTo, setTestTo] = React.useState('');
  const [busy, setBusy] = React.useState('');

  const enabled = String(settings.smtp_enabled || 'false').toLowerCase() === 'true';
  const secure = String(settings.smtp_secure || 'false').toLowerCase() === 'true';

  const verify = async () => {
    setBusy('verify'); setMsg(null);
    try {
      const r = await api.post('/settings/verify-smtp');
      setMsg({ type: 'success', text: r.data.message || 'SMTP bağlantısı başarılı' });
    } catch (e) {
      setMsg({ type: 'error', text: 'Bağlantı başarısız: ' + (e.response?.data?.message || e.message) });
    } finally { setBusy(''); }
  };

  const sendTest = async () => {
    if (!testTo) return setMsg({ type: 'error', text: 'Test için bir e-posta adresi girin' });
    setBusy('test'); setMsg(null);
    try {
      const r = await api.post('/settings/test-email', { to: testTo });
      setMsg({ type: 'success', text: r.data.message || 'Test e-postası gönderildi' });
    } catch (e) {
      setMsg({ type: 'error', text: 'Gönderilemedi: ' + (e.response?.data?.message || e.message) });
    } finally { setBusy(''); }
  };

  const savePwd = async () => {
    if (!pwd) return; // boşsa kaydetme (mevcut şifreyi koru)
    // Gmail App Password "abcd efgh ijkl mnop" formatında verilir — boşlukları temizle
    const cleaned = pwd.replace(/\s+/g, '');
    await saveSetting('smtp_password', cleaned);
    // UI flag'ini hemen güncelle — kullanıcı "Şifre tanımlı değil" görmesin
    setSettings(prev => ({ ...prev, smtp_password_set: true }));
    setPwd('');
    setMsg({ type: 'success', text: '✓ SMTP şifresi kaydedildi (' + cleaned.length + ' karakter)' });
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div style={{background:'var(--bg-card)', border:'1px solid var(--border-secondary)', borderRadius:14, padding:20, marginBottom:16}}>
      <h2 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:8}}>E-posta (SMTP)</h2>
      <p style={{fontSize:'0.78rem', color:'var(--text-tertiary)', marginBottom:16}}>
        Randevu hatırlatmaları ve sistem e-postaları için SMTP yapılandırması. Şifre maskelenir; değiştirmek için yeni şifreyi yazıp alanın dışına tıklayın.
      </p>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
        <FormField label="Aktif" hint="Kapalıyken e-posta hatırlatmaları gönderilmez.">
          <select
            value={enabled ? 'true' : 'false'}
            onChange={e => saveSetting('smtp_enabled', e.target.value)}
          >
            <option value="false">Hayır</option>
            <option value="true">Evet</option>
          </select>
        </FormField>

        <FormField label="SSL/TLS">
          <select
            value={secure ? 'true' : 'false'}
            onChange={e => saveSetting('smtp_secure', e.target.value)}
          >
            <option value="false">Hayır (port 587 / STARTTLS)</option>
            <option value="true">Evet (port 465 / SSL)</option>
          </select>
        </FormField>

        <FormField label="SMTP Host">
          <input
            value={settings.smtp_host || ''}
            onChange={e => saveSetting('smtp_host', e.target.value)}
            placeholder="örn. smtp.gmail.com"
          />
        </FormField>

        <FormField label="Port">
          <input
            type="number"
            value={settings.smtp_port || ''}
            onChange={e => saveSetting('smtp_port', e.target.value)}
            placeholder="587"
          />
        </FormField>

        <FormField label="Kullanıcı (e-posta)">
          <input
            value={settings.smtp_user || ''}
            onChange={e => saveSetting('smtp_user', e.target.value)}
            placeholder="info@firmaniz.com"
          />
        </FormField>

        <FormField label="Şifre" hint={settings.smtp_password_set ? 'Bir şifre kayıtlı. Değiştirmek için yeni değer yazın.' : 'Şifre tanımlı değil.'}>
          <input
            type="password"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            onBlur={savePwd}
            placeholder={settings.smtp_password_set ? '•••••• (mevcut)' : 'Yeni şifre girin'}
            autoComplete="new-password"
          />
        </FormField>

        <FormField label="Gönderen E-posta">
          <input
            value={settings.smtp_from_email || ''}
            onChange={e => saveSetting('smtp_from_email', e.target.value)}
            placeholder="(boş bırakılırsa kullanıcı adresi kullanılır)"
          />
        </FormField>

        <FormField label="Gönderen Adı">
          <input
            value={settings.smtp_from_name || ''}
            onChange={e => saveSetting('smtp_from_name', e.target.value)}
            placeholder="örn. Hent"
          />
        </FormField>
      </div>

      <div style={{display:'flex', gap:8, marginTop:16, flexWrap:'wrap', alignItems:'center'}}>
        <Button variant="ghost" size="sm" onClick={verify} loading={busy === 'verify'} disabled={!!busy && busy !== 'verify'}>
          Bağlantıyı Test Et
        </Button>
        <input
          className="ui-input"
          type="email"
          value={testTo}
          onChange={e => setTestTo(e.target.value)}
          placeholder="Test e-postası alıcısı"
          style={{flex:'1 1 220px'}}
        />
        <Button size="sm" onClick={sendTest} loading={busy === 'test'} disabled={(!!busy && busy !== 'test') || !testTo}>
          Test Gönder
        </Button>
      </div>
    </div>
  );
}

// ── AI Asistan Ayarları ──
const AI_DEFAULTS = {
  api_key_set: false,
  api_key_last4: null,
  provider: 'anthropic',
  model: 'claude-sonnet-4-6',
  is_enabled: false,
  monthly_limit_usd: 50,
  monthly_spent_usd: 0,
};

const PROVIDER_INFO = {
  anthropic: {
    label: 'Anthropic (Claude)',
    keyPlaceholder: 'sk-ant-...',
    consoleUrl: 'https://console.anthropic.com/',
    consoleLabel: 'console.anthropic.com',
    note: 'Premium kalite, ücretli. Stabil tool-use.',
    models: [
      { value: 'claude-sonnet-4-6',          label: 'Sonnet 4.6 (önerilen — denge)' },
      { value: 'claude-opus-4-7',            label: 'Opus 4.7 (en güçlü, pahalı)' },
      { value: 'claude-haiku-4-5-20251001',  label: 'Haiku 4.5 (hızlı, ucuz)' },
    ],
  },
  gemini: {
    label: 'Google Gemini',
    keyPlaceholder: 'AIza...',
    consoleUrl: 'https://aistudio.google.com/apikey',
    consoleLabel: 'aistudio.google.com/apikey',
    note: 'Ücretsiz tier mevcut (15 RPM, günlük 1500 istek). Hızlı başlangıç için ideal.',
    models: [
      { value: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash (önerilen — hızlı, ücretsiz tier)' },
      { value: 'gemini-2.5-pro',         label: 'Gemini 2.5 Pro (en güçlü)' },
      { value: 'gemini-2.0-flash',       label: 'Gemini 2.0 Flash (eski sürüm)' },
      { value: 'gemini-2.0-flash-lite',  label: 'Gemini 2.0 Flash Lite (en ucuz)' },
    ],
  },
};

function AiSettings({ setMsg }) {
  const [data, setData] = React.useState(AI_DEFAULTS);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [apiKey, setApiKey] = React.useState('');
  const [busy, setBusy] = React.useState('');

  const load = async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await api.get('/ai-settings');
      setData({ ...AI_DEFAULTS, ...(r.data.data || {}) });
    } catch (e) {
      setLoadError(e.response?.status === 404
        ? 'AI servisi bulunamadı — sunucunun yeniden başlatılması gerekebilir.'
        : (e.response?.data?.message || e.message || 'AI ayarları okunamadı'));
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div style={{background:'var(--bg-card)', border:'1px solid var(--border-secondary)', borderRadius:14, padding:20, marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:8}}>AI Asistan</h2>
        <div style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>Yükleniyor...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{background:'var(--bg-card)', border:'1px solid var(--border-secondary)', borderRadius:14, padding:20, marginBottom:16}}>
        <h2 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:8}}>AI Asistan (Veri Analizi)</h2>
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, color: 'var(--warning)', fontSize: '0.85rem', marginBottom: 10 }}>
          ⚠ {loadError}
        </div>
        <Button size="sm" variant="ghost" onClick={load}>Tekrar Dene</Button>
      </div>
    );
  }

  const saveField = async (patch) => {
    setBusy('save');
    try {
      await api.put('/ai-settings', patch);
      setMsg && setMsg({ type: 'success', text: 'AI ayarı kaydedildi' });
      setTimeout(() => setMsg && setMsg(null), 1800);
      await load();
    } catch (e) {
      setMsg && setMsg({ type: 'error', text: e.response?.data?.message || 'Kaydedilemedi' });
    } finally { setBusy(''); }
  };

  const saveKey = async () => {
    if (!apiKey.trim()) return;
    await saveField({ api_key: apiKey.trim() });
    setApiKey('');
  };

  const clearKey = async () => {
    if (!window.confirm('API key silinsin mi?')) return;
    await saveField({ api_key: '' });
  };

  const test = async () => {
    setBusy('test');
    try {
      const r = await api.post('/ai-settings/test');
      setMsg && setMsg({ type: 'success', text: r.data.message || 'API key geçerli' });
    } catch (e) {
      setMsg && setMsg({ type: 'error', text: e.response?.data?.message || 'Test başarısız' });
    } finally { setBusy(''); }
  };

  const provider = data.provider || 'anthropic';
  const info = PROVIDER_INFO[provider] || PROVIDER_INFO.anthropic;

  const changeProvider = async (newProvider) => {
    if (newProvider === provider) return;
    if (data.api_key_set && !window.confirm(
      `${info.label} → ${PROVIDER_INFO[newProvider].label} geçişi yapılacak.\n\n` +
      `Mevcut API key silinecek (her provider'ın key formatı farklı). Yeni key girmeniz gerekecek.\n\nDevam edilsin mi?`
    )) return;
    await saveField({ provider: newProvider });
  };

  return (
    <div style={{background:'var(--bg-card)', border:'1px solid var(--border-secondary)', borderRadius:14, padding:20, marginBottom:16}}>
      <h2 style={{fontSize:'1.1rem', fontWeight:600, marginBottom:8}}>AI Asistan (Veri Analizi)</h2>
      <p style={{fontSize:'0.78rem', color:'var(--text-tertiary)', marginBottom:16}}>
        Doğal dil ile iş verinizi sorgulayın. <strong>Sadece okuma</strong> yapar — INSERT/UPDATE/DELETE imkânsız.
        API key'iniz şifrelenerek saklanır; ücret/kota sizin <strong>{info.label}</strong> hesabınızdan düşer.
        API key almak için: <a href={info.consoleUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>{info.consoleLabel}</a>
      </p>

      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
        <FormField label="Sağlayıcı (Provider)" hint={info.note} fullWidth>
          <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
            {Object.entries(PROVIDER_INFO).map(([key, p]) => (
              <label key={key} style={{
                display:'flex', alignItems:'center', gap:8, padding:'8px 14px',
                border: '1px solid ' + (provider === key ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                background: provider === key ? 'rgba(59,130,246,0.08)' : 'var(--bg-input)',
                borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', flex: '1 1 200px',
              }}>
                <input
                  type="radio"
                  name="ai-provider"
                  checked={provider === key}
                  onChange={() => changeProvider(key)}
                  disabled={busy === 'save'}
                />
                <span style={{fontWeight: provider === key ? 600 : 400}}>{p.label}</span>
              </label>
            ))}
          </div>
        </FormField>

        <FormField label={`${info.label} API Key`} hint={data.api_key_set ? `Tanımlı (***${data.api_key_last4})` : 'Tanımlı değil'} fullWidth>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={data.api_key_set ? '(yenisini yaz veya boş bırak)' : info.keyPlaceholder}
              style={{ flex: 1 }}
            />
            <Button size="sm" onClick={saveKey} loading={busy === 'save'} disabled={!apiKey.trim()}>Kaydet</Button>
            {data.api_key_set && (
              <>
                <Button variant="ghost" size="sm" onClick={test} loading={busy === 'test'}>Test Et</Button>
                <Button variant="danger" size="sm" onClick={clearKey} disabled={!!busy}>Sil</Button>
              </>
            )}
          </div>
        </FormField>

        <FormField label="Model">
          <select
            value={data.model || (info.models[0]?.value)}
            onChange={e => saveField({ model: e.target.value })}
          >
            {info.models.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </FormField>

        <FormField label="Aylık Limit (USD)" hint="Bu limite ulaşıldığında AI Asistan duraklar">
          <input
            type="number"
            min="0"
            step="1"
            defaultValue={data.monthly_limit_usd}
            onBlur={e => {
              const v = Number(e.target.value);
              if (v !== data.monthly_limit_usd) saveField({ monthly_limit_usd: v });
            }}
          />
        </FormField>

        <FormField label="Aktif">
          <select
            value={data.is_enabled ? 'true' : 'false'}
            onChange={e => saveField({ is_enabled: e.target.value === 'true' })}
            disabled={!data.api_key_set}
          >
            <option value="true">Aktif</option>
            <option value="false">Devre Dışı</option>
          </select>
        </FormField>
      </div>

      <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)', borderRadius: 10, fontSize: '0.82rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Bu ay kullanım:</span>
          <span style={{ fontWeight: 600 }}>${(data.monthly_spent_usd || 0).toFixed(4)} / ${(data.monthly_limit_usd || 50).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── ONAY HİYERARŞİSİ ──────────────────────────────────────────────
// Modül × yön bazlı seviye tanımları. Hiyerarşi yoksa belge otomatik onaylı,
// varsa belge "Bekliyor" durumunda kuyruğa girer.
function ApprovalHierarchySection() {
  const [list, setList] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [editing, setEditing] = React.useState(null);  // hiyerarşi objesi (id varsa update)
  const [msg, setMsg] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  const MODULES = [
    { module: 'invoice', direction: 'sales',    label: 'Satış Faturası' },
    { module: 'invoice', direction: 'purchase', label: 'Alış Faturası' },
    { module: 'waybill', direction: 'sales',    label: 'Satış İrsaliyesi' },
    { module: 'waybill', direction: 'purchase', label: 'Alış İrsaliyesi' },
    { module: 'order',   direction: 'sales',    label: 'Alınan Sipariş' },
    { module: 'order',   direction: 'purchase', label: 'Verilen Sipariş' },
    { module: 'quote',   direction: 'sales',    label: 'Verilen Teklif' },
    { module: 'quote',   direction: 'purchase', label: 'Alınan Teklif' },
  ];

  const load = async () => {
    try {
      const [h, u, r] = await Promise.all([
        api.get('/approvals/hierarchies'),
        api.get('/users').catch(() => ({ data: { data: { items: [] } } })),
        api.get('/roles').catch(() => ({ data: { data: [] } })),
      ]);
      if (h.data.success) setList(h.data.data || []);
      setUsers(u.data?.data?.items || u.data?.data || []);
      setRoles(r.data?.data || []);
    } catch (e) { console.error(e); }
  };
  React.useEffect(() => { load(); }, []);

  const startNew = (cfg) => {
    setEditing({
      id: null, module: cfg.module, direction: cfg.direction,
      name: cfg.label, is_active: true,
      levels: [{ level_no: 1, name: 'Seviye 1', min_amount: '', approver_user_ids: [], approver_role_ids: [], require_all: false }],
    });
  };

  const startEdit = async (id) => {
    try {
      const r = await api.get('/approvals/hierarchies/' + id);
      if (r.data.success) {
        const d = r.data.data;
        setEditing({
          ...d,
          levels: (d.levels || []).map(lv => ({
            ...lv,
            approver_user_ids: lv.approver_user_ids ? JSON.parse(lv.approver_user_ids) : [],
            approver_role_ids: lv.approver_role_ids ? JSON.parse(lv.approver_role_ids) : [],
            min_amount: lv.min_amount == null ? '' : lv.min_amount,
          })),
        });
      }
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.levels?.length) { setMsg({ type: 'error', text: 'En az bir seviye gerekli' }); return; }
    setSaving(true);
    try {
      await api.post('/approvals/hierarchies', editing);
      setMsg({ type: 'success', text: 'Hiyerarşi kaydedildi' });
      setEditing(null);
      await load();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Hata' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Hiyerarşi silinsin mi?')) return;
    try {
      await api.delete('/approvals/hierarchies/' + id);
      await load();
    } catch (e) { setMsg({ type: 'error', text: 'Silinemedi' }); }
  };

  const updateLevel = (idx, patch) => {
    setEditing(p => ({ ...p, levels: p.levels.map((lv, i) => i === idx ? { ...lv, ...patch } : lv) }));
  };
  const addLevel = () => {
    setEditing(p => ({ ...p, levels: [...p.levels, { level_no: p.levels.length + 1, name: 'Seviye ' + (p.levels.length + 1), min_amount: '', approver_user_ids: [], approver_role_ids: [], require_all: false }] }));
  };
  const removeLevel = (idx) => {
    setEditing(p => ({ ...p, levels: p.levels.filter((_, i) => i !== idx).map((lv, i) => ({ ...lv, level_no: i + 1 })) }));
  };

  const findHier = (cfg) => list.find(h => h.module === cfg.module && h.direction === cfg.direction);

  return (
    <div>
      <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', fontWeight: 600 }}>✅ Onay Hiyerarşisi</h2>
      <div style={{ marginBottom: 12, padding: 10, background: 'rgba(59,130,246,0.08)', borderLeft: '3px solid var(--info)', borderRadius: 4, fontSize: 13 }}>
        💡 Hiyerarşi tanımlanmış belge tipleri kaydedildiğinde onay sürecine girer. Tanımlanmayan modüllerde belge otomatik onaylı kabul edilir.
      </div>
      {msg && (
        <div style={{ marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
          background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
          color: msg.type === 'error' ? 'var(--danger)' : '#0d9488' }}>{msg.text}</div>
      )}

      {/* Modül listesi — her satır */}
      <div style={{ display: 'grid', gap: 8 }}>
        {MODULES.map(cfg => {
          const h = findHier(cfg);
          return (
            <div key={cfg.module + '-' + cfg.direction} style={{
              padding: '10px 14px', border: '1px solid var(--border-secondary)', borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <strong style={{ flex: '1 0 200px', fontSize: '0.9rem' }}>{cfg.label}</strong>
              {h ? (
                <>
                  <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--r-full)', background: 'rgba(16,185,129,0.15)', color: 'var(--success)', fontWeight: 700 }}>
                    {h.level_count || 0} seviye {h.is_active ? '✓' : '(pasif)'}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(h.id)}>Düzenle</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(h.id)} style={{ color: 'var(--danger)' }}>Sil</Button>
                </>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => startNew(cfg)}>+ Hiyerarşi Tanımla</Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Düzenleme paneli */}
      {editing && (
        <div style={{ marginTop: 16, padding: 16, border: '2px solid var(--accent-primary)', borderRadius: 10, background: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <strong style={{ flex: 1 }}>{editing.name} ({editing.module} × {editing.direction})</strong>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.84rem', marginRight: 12 }}>
              <input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing(p => ({ ...p, is_active: e.target.checked }))} />
              Aktif
            </label>
          </div>
          {editing.levels.map((lv, idx) => {
            const toggleUser = (uid) => {
              const cur = lv.approver_user_ids || [];
              updateLevel(idx, { approver_user_ids: cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid] });
            };
            const toggleRole = (rid) => {
              const cur = lv.approver_role_ids || [];
              updateLevel(idx, { approver_role_ids: cur.includes(rid) ? cur.filter(x => x !== rid) : [...cur, rid] });
            };
            const userCount = (lv.approver_user_ids || []).length;
            const roleCount = (lv.approver_role_ids || []).length;
            return (
              <div key={idx} style={{ marginBottom: 10, padding: 12, background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 6 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 12, color: 'var(--accent-primary)' }}>SEVİYE {lv.level_no}</strong>
                  <input value={lv.name} onChange={e => updateLevel(idx, { name: e.target.value })} placeholder="Seviye adı" style={{ flex: '1 1 200px' }} />
                  <input type="number" value={lv.min_amount} onChange={e => updateLevel(idx, { min_amount: e.target.value })} placeholder="Min. tutar (boş=her tutar)" style={{ width: 200 }} />
                  <Button size="sm" variant="ghost" onClick={() => removeLevel(idx)} style={{ color: 'var(--danger)' }}>× Seviyeyi Sil</Button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                  {/* Kullanıcılar checkbox listesi */}
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.4 }}>
                      👤 Onaycı Kullanıcılar <span style={{ color: 'var(--accent-primary)' }}>({userCount} seçili)</span>
                    </div>
                    <div style={{
                      maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-input)',
                      borderRadius: 6, padding: 8, background: 'var(--bg-input)',
                    }}>
                      {users.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', padding: 8 }}>Kullanıcı yok</div>
                      ) : users.map(u => (
                        <label key={u.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                          borderRadius: 4, cursor: 'pointer', fontSize: 13,
                          background: (lv.approver_user_ids || []).includes(u.id) ? 'rgba(99,102,241,0.10)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (!(lv.approver_user_ids || []).includes(u.id)) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                        onMouseLeave={e => { if (!(lv.approver_user_ids || []).includes(u.id)) e.currentTarget.style.background = 'transparent'; }}>
                          <input type="checkbox" checked={(lv.approver_user_ids || []).includes(u.id)} onChange={() => toggleUser(u.id)} />
                          <span style={{ flex: 1, fontWeight: 500 }}>{u.full_name || u.username}</span>
                          {u.username && u.full_name && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>@{u.username}</span>}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Roller checkbox listesi */}
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.4 }}>
                      🎭 Onaycı Roller <span style={{ color: 'var(--accent-primary)' }}>({roleCount} seçili)</span>
                    </div>
                    <div style={{
                      maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-input)',
                      borderRadius: 6, padding: 8, background: 'var(--bg-input)',
                    }}>
                      {roles.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'center', padding: 8 }}>Rol yok</div>
                      ) : roles.map(r => (
                        <label key={r.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px',
                          borderRadius: 4, cursor: 'pointer', fontSize: 13,
                          background: (lv.approver_role_ids || []).includes(r.id) ? 'rgba(99,102,241,0.10)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (!(lv.approver_role_ids || []).includes(r.id)) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                        onMouseLeave={e => { if (!(lv.approver_role_ids || []).includes(r.id)) e.currentTarget.style.background = 'transparent'; }}>
                          <input type="checkbox" checked={(lv.approver_role_ids || []).includes(r.id)} onChange={() => toggleRole(r.id)} />
                          <span style={{ flex: 1, fontWeight: 500 }}>{r.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', marginTop: 10 }}>
                  <input type="checkbox" checked={!!lv.require_all} onChange={e => updateLevel(idx, { require_all: e.target.checked })} />
                  Tüm onaycıların onayı gerekli <span style={{ color: 'var(--text-tertiary)' }}>(varsayılan: birinin onayı yeterli)</span>
                </label>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Button size="sm" variant="ghost" onClick={addLevel}>+ Seviye Ekle</Button>
            <span style={{ flex: 1 }} />
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>İptal</Button>
            <Button size="sm" onClick={save} loading={saving}>Kaydet</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
